import {gql} from '@apollo/client';
import {objectFromEntries} from '@benchling/js-utils';
import {Color} from '@benchling/nucleus-ui';
import React, {useState, useCallback} from 'react';

import {CoreObject, MaybeCoreObject, DomainGraphInterface} from 'app-core/framework/CoreObjectConstants';
import {getCoreObjectsWithInterface} from 'app-core/framework/CoreObjectIntrospectionUtils';
import refreshListings from 'app-core/platform/listable/refreshListings';
import {starItems, unstarItems} from 'app-core/platform/starrable/StarDAO';
import {doQuerySafe} from 'graphql-client/ApolloWrappers';
import {QueryResultType, useSkippableBenchlingQuery} from 'graphql-client/BenchlingQuery';
import {getFragmentWrapper} from 'graphql-client/declarative-translations/external/getFragmentWrapper';
import {accessibleObjs} from 'graphql-client/InaccessibleInfo';
import StarIcon from 'nav/sidepanel/StarIcon';
import EntityStore from 'stores/EntityStore';
import BenchlingDuotoneIcon from 'ui-library/icons/BenchlingDuotoneIcon';

import {StarrableItemsQuery, StarrableItemsQueryVariables} from './__generated/StarrableItemsQuery';

export const STARRED_ITEMS_QUERY = gql`
  query StarrableItemsQuery($ids: [StrictString!]!) @skipAudit @engComponent(name: CORE_OBJECT) {
    starrableItems(ids: $ids) {
      ...StarrableGraphQL
      ...InaccessibleInfoGraphQL
    }
  }
  ${getFragmentWrapper('Starrable').fragment}
  ${getFragmentWrapper('InaccessibleInfo').fragment}
`;

export interface StarredItemListingExtras {
  starredItemsByObjectId?: StarredItemsByObjectId;
}

export type StarredItemsByObjectId = Record<string, boolean>;

interface StarredItemsQueryResponse {
  type: QueryResultType;
  starredItemsByObjectId: StarredItemsByObjectId;
  refetch: () => void;
}

export function useStarredItemsQuery(items: Array<MaybeCoreObject>): StarredItemsQueryResponse {
  const ids = getCoreObjectsWithInterface(items, DomainGraphInterface.STARRABLE).map(({id}) => id);
  const result = useSkippableBenchlingQuery<StarrableItemsQuery, StarrableItemsQueryVariables>(
    STARRED_ITEMS_QUERY,
    {
      skip: ids.length === 0 || !EntityStore.currentUser(),
      variables: {ids},
      renderCachedDataWhenRefetching: true,
    }
  );

  const baseResponse = {type: result.type, refetch: result.refetch};
  if (
    result.type === QueryResultType.PENDING ||
    result.type === QueryResultType.ERROR ||
    result.type === QueryResultType.SKIPPED
  ) {
    return {...baseResponse, starredItemsByObjectId: {}};
  } else {
    return {
      ...baseResponse,
      starredItemsByObjectId: objectFromEntries(
        accessibleObjs(result.data.starrableItems).map((item) => [item.id, item.star?.isStarred || false])
      ),
    };
  }
}

export type Starrable = CoreObject;

export interface ToggleStarOptions {
  item: Starrable;
  isStarred: boolean;
}

export async function handleToggleStar({item, isStarred}: ToggleStarOptions): Promise<void> {
  const action = isStarred ? unstarItems : starItems;
  await action([item]);

  await doQuerySafe<StarrableItemsQuery, StarrableItemsQueryVariables>(
    {query: STARRED_ITEMS_QUERY},
    {variables: {ids: [item.id]}, fetchPolicy: 'network-only'}
  );

  refreshListings();
}

export type CoreObjectStarIconProps = ToggleStarOptions;

function CoreObjectStarIcon({item, isStarred}: CoreObjectStarIconProps): JSX.Element {
  const [isLoading, setIsLoading] = useState(false);

  const onClickHandler = useCallback(async () => {
    setIsLoading(true);
    await handleToggleStar({item, isStarred});
    setIsLoading(false);
  }, [item, isStarred]);

  if (isLoading) {
    return (
      <BenchlingDuotoneIcon
        icon="spinner-third"
        primaryColor={Color.GRAY_600}
        secondaryColor={Color.GRAY_200}
        spin={true}
      />
    );
  } else {
    return <StarIcon onClick={onClickHandler} starred={isStarred} />;
  }
}

export default function CoreObjectStarIconRenderer({item, isStarred}: CoreObjectStarIconProps): JSX.Element {
  return EntityStore.currentUser() && <CoreObjectStarIcon item={item} isStarred={isStarred} />;
}
